import { useState, useMemo, useCallback } from 'react';
import { Contact } from '../../../types';
import { createModuleLogger } from '../../../utils/logger';
import { BulkAction } from '../../../components/shared/BulkActionsBar';
import { TrashIcon, DownloadIcon } from '../../../components/icons';
import { ContactExportFormat } from '../../../services/modules/contactExportService';

const log = createModuleLogger('[ContactBulk]');

interface UseContactBulkActionsProps {
    getSelectedItems: () => Contact[];
    clearSelection: () => void;
    deleteContact: (id: string) => Promise<void>;
    onExportSelected?: (format: ContactExportFormat) => void;
    canExport?: boolean;
}

export function useContactBulkActions({
    getSelectedItems,
    clearSelection,
    deleteContact,
    onExportSelected,
    canExport = false,
}: UseContactBulkActionsProps) {
    const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);

    const handleBulkDelete = useCallback(async () => {
        const selectedContacts = getSelectedItems();
        try {
            for (const contact of selectedContacts) {
                await deleteContact(contact.id);
            }
            clearSelection();
            setBulkDeleteModalOpen(false);
        } catch (error) {
            log.error('Failed to delete contacts:', error);
        }
    }, [getSelectedItems, clearSelection, deleteContact]);

    const bulkActions: BulkAction[] = useMemo(() => {
        const actions: BulkAction[] = [];

        if (canExport && onExportSelected) {
            actions.push({
                label: 'Exportar',
                icon: DownloadIcon,
                type: 'dropdown',
                options: [
                    { id: 'csv', label: 'CSV (.csv)' },
                    { id: 'xlsx', label: 'Excel (.xlsx)' },
                ],
                onSelectOption: (optionId) => onExportSelected(optionId as ContactExportFormat),
            });
        }

        actions.push({
            label: 'Excluir',
            icon: TrashIcon,
            onClick: () => setBulkDeleteModalOpen(true),
            variant: 'danger' as const,
        });

        return actions;
    }, [canExport, onExportSelected]);

    return {
        bulkActions,
        isBulkDeleteModalOpen,
        setBulkDeleteModalOpen,
        handleBulkDelete,
    };
}
